import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { HERO } from "@/constants/testIds";
import { PROJECTS } from "@/lib/data";

const EASE = [0.76, 0, 0.24, 1];

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="line-mask">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, ease: EASE, delay }}
      className={className}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-24%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  // Scroll-linked parallax translate ONLY, the scale is driven by the
  // continuous cinematic dolly below so the two never fight.
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  // Primary hero image, NORD exterior (photographic)
  const heroImg = PROJECTS.find((p) => p.slug === "nord")?.images?.[0] ?? PROJECTS[0].images[0];

  return (
    <section
      id="hero"
      ref={ref}
      data-testid={HERO.root}
      className="relative w-full h-[100svh] overflow-hidden bg-[#0a0a0b]"
    >
      {/* Full-bleed hero photograph, held still. Fades in AFTER the headline lands so
          the black frame + copy read first (notahotel.com rhythm). */}
      <motion.div
        style={{ y: imgY }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
        className="absolute inset-0 z-0"
        aria-hidden="true"
      >
        <div className="w-full h-full">
          <picture>
            <source
              media="(max-width: 767px)"
              srcSet="https://customer-assets-7cd3h4nn.emergentagent.net/job_depth-design-studio/artifacts/09m7w7wf_midjourney-editor-1777819576112.png"
            />
            <img
              src={heroImg?.src ?? heroImg}
              alt={heroImg?.alt ?? ""}
              className="w-full h-full object-cover"
              fetchPriority="high"
            />
          </picture>
        </div>
      </motion.div>

      {/* Editorial vignette + gradient */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,11,0.55) 0%, rgba(10,10,11,0.15) 32%, rgba(10,10,11,0.35) 65%, rgba(10,10,11,0.92) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 65% 40%, rgba(0,0,0,0) 0%, rgba(10,10,11,0.25) 55%, rgba(10,10,11,0.75) 100%)",
        }}
      />

      {/* R3F accent removed, hero image now takes the whole screen */}

      {/* Main headline */}
      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-28 px-6 md:px-10"
      >
        <h1
          data-testid={HERO.title}
          className="font-display !normal-case text-white leading-[0.9] tracking-[-0.03em] text-[11vw] md:text-[7vw] lg:text-[6vw] max-w-none md:max-w-[85%]"
          style={{ fontVariationSettings: '"opsz" 144' }}
        >
          {/* Desktop / tablet: two lines */}
          <span className="hidden md:block">
            <Line delay={0.35}>Building Worlds</Line>
            <Line delay={0.5}>for the Discerning</Line>
          </span>
          {/* Mobile only: each word group on its own line */}
          <span className="block md:hidden">
            <Line delay={0.3}>Building</Line>
            <Line delay={0.4}>Worlds</Line>
            <Line delay={0.5}>for the</Line>
            <Line delay={0.6}>Discerning</Line>
          </span>
        </h1>

        <div className="mt-4 md:mt-6 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
            className="font-sans-hd text-white/85 text-sm md:text-xl leading-relaxed md:whitespace-nowrap"
          >
            Narrative-driven hospitality experiences anchored{" "}
            <br className="md:hidden" />
            in story, design, and place.
          </motion.p>

          <motion.button
            data-testid={HERO.scrollCue}
            data-cursor="↓"
            onClick={() => {
              const el = document.getElementById("approach") || document.getElementById("work");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="group flex items-center gap-3 mono-label text-white border-b border-white/40 pb-2 hover:border-white transition-colors"
            aria-label="Scroll to enter"
          >
            <span>Scroll to enter</span>
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block"
            >
              ↓
            </motion.span>
          </motion.button>
        </div>
      </motion.div>

    </section>
  );
}
